/** Order-2 (9-coefficient) spherical harmonics of sky radiance, in three.js SphericalHarmonics3 convention. */
import type { Vec3 } from '../frame';
import type { RGB } from '../skyPalette';

export function fibonacciDirections(n: number): Vec3[] {
  const golden = Math.PI * (3 - Math.sqrt(5));
  return Array.from({ length: n }, (_, i) => {
    const y = 1 - (2 * (i + 0.5)) / n;
    const r = Math.sqrt(1 - y * y);
    const phi = golden * i;
    return [Math.cos(phi) * r, y, Math.sin(phi) * r];
  });
}

/** Real SH basis (same order/constants as three.js SphericalHarmonics3.getBasisAt). */
export function shBasis([x, y, z]: Vec3): number[] {
  return [
    0.282095,
    0.488603 * y,
    0.488603 * z,
    0.488603 * x,
    1.092548 * x * y,
    1.092548 * y * z,
    0.315392 * (3 * z * z - 1),
    1.092548 * x * z,
    0.546274 * (x * x - y * y),
  ];
}

/** Monte-Carlo projection over uniform directions (weight 4π/N). Returns 9 RGB coefficients. */
export function projectSH9(sample: (dir: Vec3) => RGB, n = 256): RGB[] {
  const coeffs: RGB[] = Array.from({ length: 9 }, () => [0, 0, 0]);
  const w = (4 * Math.PI) / n;
  for (const d of fibonacciDirections(n)) {
    const c = sample(d);
    const basis = shBasis(d);
    for (let k = 0; k < 9; k++) {
      coeffs[k]![0] += c[0] * basis[k]! * w;
      coeffs[k]![1] += c[1] * basis[k]! * w;
      coeffs[k]![2] += c[2] * basis[k]! * w;
    }
  }
  return coeffs;
}

/** Reconstructed radiance in a direction. */
export function evalSH9(coeffs: RGB[], d: Vec3): RGB {
  const basis = shBasis(d);
  const out: RGB = [0, 0, 0];
  for (let k = 0; k < 9; k++) {
    out[0] += coeffs[k]![0] * basis[k]!;
    out[1] += coeffs[k]![1] * basis[k]!;
    out[2] += coeffs[k]![2] * basis[k]!;
  }
  return out;
}
